import { useEffect, useState, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { API, BACKEND_URL } from "@/lib/api";

// Configure axios to always send auth token
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("auth_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Track page visits
const trackPageVisit = (page) => {
  axios.post(`${API}/analytics/track`, {
    page,
    referrer: document.referrer
  }).catch(() => { }); // Silent fail
};

// Pages
import LandingPage from "@/pages/LandingPage";
import Dashboard from "@/pages/Dashboard";
import EventControlCenter from "@/pages/EventControlCenter";
import EventDetail from "@/pages/EventDetailHeroNew";
// Legacy imports kept for reference
// import EventDetailOld from "@/pages/EventDetailNew";
// import EventDetailNewDesign from "@/pages/EventDetailNewDesign";
// import EventDetailComplete from "@/pages/EventDetailComplete";
// import EventDetailHero from "@/pages/EventDetailHero";
import CreateEvent from "@/pages/CreateEvent";
import PublicEvent from "@/pages/PublicEventHeroNew";
// import PublicEventNew from "@/pages/PublicEventNew";
// Legacy imports
// import PublicEventOld from "@/pages/PublicEventHeroNew";
// import PublicEventHero from "@/pages/PublicEventHero";
import Profile from "@/pages/Profile";
import AdminDashboard from "@/pages/AdminDashboardNew";
import AdminFeatureSettings from "@/pages/AdminFeatureSettings";
import AdminTransactions from "@/pages/AdminTransactions";
import Pricing from "@/pages/Pricing";
import PaymentResult from "@/pages/PaymentResult";
import EventChat from "@/pages/EventChat";
import PackageSelection from "@/pages/PackageSelection";
import PaymentSuccess from "@/pages/PaymentSuccess";
import Impressum from "@/pages/Impressum";
import Datenschutz from "@/pages/Datenschutz";
import Kontakt from "@/pages/Kontakt";
import Features from "@/pages/Features";
import AuthRegister from "@/pages/AuthRegister";
import AGB from "@/pages/AGB";
import FAQ from "@/pages/FAQ";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import MaintenancePage from "@/pages/MaintenancePage";
import MediaUploadPage from "@/pages/MediaUploadPage";
import BottomNavigation from "@/components/BottomNavigation";

// Scroll to Top on Route Change
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  // Force Light Mode globally
  useEffect(() => {
    document.documentElement.classList.remove("dark");
  }, []);

  return null;
}

// Conditional Footer - hide on certain routes
function ConditionalFooter() {
  const { pathname } = useLocation();

  // Routes where footer should NOT appear (they have their own layout)
  const noFooterRoutes = [
    '/',        // Landing page has its own footer
    '/features', // Features page has its own footer
    '/login',   // Login page has its own layout
    '/auth/',   // Auth callback pages
    '/event/',  // EventChat and EventDetail
    '/e/',      // Public events
    '/dashboard',
    '/profile',
    '/wp-admin',
    '/admin',
    '/create-event',
    '/select-package',
  ];

  // Check if current path should hide footer
  const shouldHideFooter = noFooterRoutes.some(route => {
    if (route === '/') return pathname === '/';
    return pathname.startsWith(route);
  });

  if (shouldHideFooter) return null;
  return <AppFooter />;
}

// Components
import CookieBanner from "@/components/CookieBanner";
import AppFooter from "@/components/AppFooter";
import BrandingProvider from "@/components/BrandingProvider";
import { LanguageProvider } from "@/contexts/LanguageContext";




// Auth context
export const AuthContext = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [maintenance, setMaintenance] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Check maintenance status
  const checkMaintenance = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/maintenance/status`);
      // Ensure response data is an object
      if (response.data && typeof response.data === 'object') {
        setMaintenance(response.data);
        return response.data;
      }
      throw new Error("Invalid maintenance data");
    } catch (error) {
      setMaintenance({ enabled: false });
      return { enabled: false };
    }
  }, []);

  const checkAuth = useCallback(async () => {
    console.log("checkAuth: Starting authentication check...");
    try {
      // First check for JWT token in localStorage (new Google OAuth)
      const token = localStorage.getItem("auth_token");

      if (token) {
        // Special case for local development bypass
        if (token === "bypass_token_active") {
          const cachedUser = localStorage.getItem("user");
          if (cachedUser) {
            const parsedUser = JSON.parse(cachedUser);
            setUser(parsedUser);
            setLoading(false);
            return parsedUser;
          }
        }

        // Also check if we have cached user data
        const cachedUser = localStorage.getItem("user");

        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { "Authorization": `Bearer ${token}` }
          });

          // CRITICAL: Validate that we actually got a user object, not HTML (from a 404/500 rewrite)
          if (response.data && typeof response.data === 'object' && (response.data.id || response.data.email)) {
            setUser(response.data);
            // Update cached user
            localStorage.setItem("user", JSON.stringify(response.data));
            return response.data;
          } else {
            console.error("checkAuth: /api/auth/me returned invalid data (possibly HTML):", response.data);
            throw new Error("Invalid user data received");
          }
        } catch (authError) {
          console.error("checkAuth: /api/auth/me FAILED:", authError.response?.status, authError.message);

          // If we have a cached user and token exists, use cached user as fallback
          if (cachedUser) {
            try {
              const parsedUser = JSON.parse(cachedUser);
              console.log("checkAuth: Using cached user as fallback:", parsedUser?.email);

              // Only clear token if it's definitely expired (401)
              if (authError.response?.status === 401) {
                console.log("checkAuth: Token expired (401), clearing credentials");
                localStorage.removeItem("auth_token");
                localStorage.removeItem("user");
                setUser(null);
                return null;
              }

              // For other errors (network, 500, etc), use cached user
              setUser(parsedUser);
              return parsedUser;
            } catch (parseError) {
              console.error("checkAuth: Failed to parse cached user");
            }
          }

          // No cached user and API failed - clear and return null
          localStorage.removeItem("auth_token");
          localStorage.removeItem("user");
          setUser(null);
          return null;
        }
      }

      // No token - we are logged out
      console.log("checkAuth: No JWT token found.");
      setUser(null);
      return null;
    } catch (error) {
      console.log("checkAuth: Auth failed or timed out.");
      localStorage.removeItem("auth_token");
      localStorage.removeItem("user");
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Expose checkAuth globally for components that need to refresh auth state (like PackageSelection)
  useEffect(() => {
    window.checkAuth = checkAuth;
    return () => {
      delete window.checkAuth;
    };
  }, [checkAuth]);

  const processSessionId = useCallback(async (sessionId) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/auth/session?session_id=${sessionId}`);

      // CRITICAL: Validate that we actually got a user object, not HTML (from a 404/500 rewrite)
      if (response.data && typeof response.data === 'object' && response.data.success) {
        setUser(response.data.user);
        toast.success("Erfolgreich angemeldet!");
        // Clean URL and redirect to dashboard
        window.history.replaceState({}, document.title, "/dashboard");
        navigate("/dashboard");
      } else {
        console.error("processSessionId: /api/auth/session returned invalid data (possibly HTML):", response.data);
        throw new Error("Invalid session data received");
      }
    } catch (error) {
      console.error("Session processing error:", error);
      toast.error("Anmeldung fehlgeschlagen");
      window.history.replaceState({}, document.title, "/");
      navigate("/");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    // Check for bypass token in URL
    const params = new URLSearchParams(location.search);
    const urlBypass = params.get("bypass") || params.get("admin_bypass");
    if (urlBypass === "moki-emergency-2024" || urlBypass === "true") {
      localStorage.setItem("moki_maintenance_bypass", "true");
      // Remove param from URL
      const newParams = new URLSearchParams(location.search);
      newParams.delete("bypass");
      newParams.delete("admin_bypass");
      const newSearch = newParams.toString();
      navigate(location.pathname + (newSearch ? `?${newSearch}` : ""), { replace: true });
    }
  }, [location, navigate]);

  useEffect(() => {
    // Check maintenance status first
    checkMaintenance();

    // Check for session_id in URL hash
    const hash = window.location.hash;
    if (hash.includes("session_id=")) {
      const sessionId = hash.split("session_id=")[1]?.split("&")[0];
      if (sessionId) {
        processSessionId(sessionId);
        return;
      }
    }

    // Otherwise check existing session
    checkAuth();
  }, [checkAuth, processSessionId, checkMaintenance]);

  const logout = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      await axios.post(`${API}/auth/logout`, {}, {
        headers: token ? { "Authorization": `Bearer ${token}` } : {}
      });
      // Clear local storage
      localStorage.removeItem("auth_token");
      localStorage.removeItem("user");
      setUser(null);
      toast.success("Erfolgreich abgemeldet");
      navigate("/");
    } catch (error) {
      console.error("Logout error:", error);
      // Clear anyway
      localStorage.removeItem("auth_token");
      localStorage.removeItem("user");
      setUser(null);
      navigate("/");
    }
  };

  const login = () => {
    // Use new custom Google OAuth login page
    navigate("/login");
  };

  const handleLoginSuccess = (userData, token) => {
    setUser(userData);
    toast.success("Erfolgreich angemeldet!");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Laden...</p>
        </div>
      </div>
    );
  }

  // Show maintenance page if enabled (but allow admins if configured)
  if (maintenance?.enabled) {
    // Check multiple admin indicators for robustness
    const cachedUser = JSON.parse(localStorage.getItem("user") || "{}");
    const isAdmin = user?.is_admin || user?.role === "admin" || cachedUser?.is_admin || cachedUser?.role === "admin";
    const hasBypass = localStorage.getItem("moki_maintenance_bypass") === "true";

    // Always allow access to login page so admin can login
    const allowedPaths = ["/login", "/auth/callback"];
    const isAllowedPath = allowedPaths.some(path => location.pathname.startsWith(path));

    // Priority for bypass and admins
    // Note: order is important here
    if (hasBypass || (isAdmin && (maintenance.allow_admins !== false))) {
      // Access granted
    } else if (isAllowedPath) {
      // Allow login page access
    } else {
      // Show maintenance page
      return <MaintenancePage message={maintenance.message} expectedEnd={maintenance.expected_end} />;
    }
  }

  return children({ user, login, logout, checkAuth, maintenance });
};

// Protected Route wrapper with package check
const ProtectedRoute = ({ children, user, requirePackage = false }) => {
  // Check if we have a token but user is null (might be loading)
  const hasToken = localStorage.getItem("auth_token");
  const hasCachedUser = localStorage.getItem("user");

  if (!user) {
    // If we have a token and cached user, don't redirect yet
    // This prevents redirect loop during page reload
    if (hasToken && hasCachedUser) {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Laden...</p>
          </div>
        </div>
      );
    }
    return <Navigate to="/login" replace />;
  }

  // Package requirement check
  // Redirect to package selection if user has no package and we are not on the selection page
  const location = useLocation();
  const isPackageSelectionPage = location.pathname === "/select-package" || location.pathname === "/pricing" || location.pathname === "/packages";

  if (user && user.package_status === "none" && !isPackageSelectionPage) {
    console.log("ProtectedRoute: User has no package, redirecting to /select-package");
    return <Navigate to="/select-package" replace />;
  }

  return children;
};

// Page tracking component
function PageTracker() {
  const location = useLocation();

  useEffect(() => {
    trackPageVisit(location.pathname);
  }, [location.pathname]);

  return null;
}

function AppRoutes() {
  return (
    <AuthContext>
      {({ user, login, logout }) => (
        <>
          <PageTracker />
          <ScrollToTop />
          <div className="paper-texture" />
          <Routes>
            <Route
              path="/"
              element={
                user ? <Navigate to="/dashboard" replace /> : <LandingPage onLogin={login} />
              }
            />
            <Route
              path="/features"
              element={<Features onLogin={login} />}
            />
            <Route
              path="/auth/register"
              element={<AuthRegister />}
            />
            <Route
              path="/login"
              element={
                user ? <Navigate to="/dashboard" replace /> :
                  (localStorage.getItem("auth_token") ? <Navigate to="/dashboard" replace /> : <Login />)
              }
            />
            <Route
              path="/auth/callback"
              element={<AuthCallback />}
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute user={user}>
                  <Dashboard user={user} onLogout={logout} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/event/new"
              element={
                <ProtectedRoute user={user}>
                  <CreateEvent user={user} onLogout={logout} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/event/:eventId"
              element={
                <ProtectedRoute user={user}>
                  <EventControlCenter user={user} onLogout={logout} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/events/:eventId"
              element={
                <ProtectedRoute user={user}>
                  <EventDetail user={user} onLogout={logout} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/event/:eventId/upload"
              element={
                <ProtectedRoute user={user}>
                  <MediaUploadPage user={user} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute user={user}>
                  <Profile user={user} onLogout={logout} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute user={user}>
                  <Navigate to="/profile" replace />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute user={user}>
                  <AdminDashboard user={user} onLogout={logout} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/dashboard"
              element={
                <ProtectedRoute user={user}>
                  <AdminDashboard user={user} onLogout={logout} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/settings"
              element={
                <ProtectedRoute user={user}>
                  <AdminDashboard user={user} onLogout={logout} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/branding"
              element={
                <ProtectedRoute user={user}>
                  <AdminDashboard user={user} onLogout={logout} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/wp-admin"
              element={
                <ProtectedRoute user={user}>
                  <Navigate to="/admin" replace />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/features"
              element={
                <ProtectedRoute user={user}>
                  <AdminFeatureSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/select-package"
              element={
                <ProtectedRoute user={user} requirePackage={false}>
                  <PackageSelection />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/features"
              element={
                <ProtectedRoute user={user}>
                  <AdminFeatureSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/transactions"
              element={
                <ProtectedRoute user={user}>
                  <AdminTransactions />
                </ProtectedRoute>
              }
            />
            <Route
              path="/pricing"
              element={<Pricing user={user} />}
            />
            <Route
              path="/payment/success"
              element={<PaymentSuccess />}
            />
            <Route
              path="/payment/cancelled"
              element={<PaymentSuccess />}
            />
            <Route
              path="/event/:eventId/chat"
              element={
                <ProtectedRoute user={user}>
                  <EventChat user={user} />
                </ProtectedRoute>
              }
            />
            <Route path="/e/:shareCode" element={<PublicEvent user={user} />} />
            <Route path="/packages" element={<PackageSelection />} />
            <Route path="/impressum" element={<Impressum />} />
            <Route path="/datenschutz" element={<Datenschutz />} />
            <Route path="/kontakt" element={<Kontakt />} />
            <Route path="/agb" element={<AGB />} />
            <Route path="/faq" element={<FAQ />} />
          </Routes>
          <ConditionalFooter />
          <BottomNavigation user={user} />
          <CookieBanner />
          <Toaster position="top-center" richColors />
        </>
      )}
    </AuthContext>
  );
}

function App() {
  return (
    <LanguageProvider>
      <BrandingProvider>
        <div className="App min-h-screen bg-background flex flex-col">
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </div>
      </BrandingProvider>
    </LanguageProvider>
  );
}

export default App;
